import React, { Component, ReactNode } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Alert,
  AlertTitle,
  Stack,
  Collapse
} from '@mui/material';
import { 
  RefreshOutlined as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  BugReport as BugReportIcon 
} from '@mui/icons-material';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  showDetails?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  showErrorDetails: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showErrorDetails: false
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo
    });

    // Log error to monitoring service
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // In production, you might want to send this to an error reporting service
    if (process.env.NODE_ENV === 'production') {
      // Example: Send to error monitoring service
      // errorReportingService.captureException(error, { extra: errorInfo });
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showErrorDetails: false
    });
  };

  toggleErrorDetails = () => {
    this.setState(prevState => ({
      showErrorDetails: !prevState.showErrorDetails
    }));
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            p: 3
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              maxWidth: 600,
              width: '100%',
              textAlign: 'center'
            }}
          >
            <Stack spacing={3} alignItems="center">
              <BugReportIcon 
                sx={{ 
                  fontSize: 64, 
                  color: 'error.main' 
                }} 
              />
              
              <Typography variant="h5" component="h1" gutterBottom>
                Oops! Something went wrong
              </Typography>
              
              <Typography variant="body1" color="text.secondary">
                We encountered an unexpected error. Don't worry, our team has been notified.
              </Typography>

              <Alert severity="error" sx={{ width: '100%', textAlign: 'left' }}>
                <AlertTitle>Error Details</AlertTitle>
                {this.state.error?.message || 'An unknown error occurred'}
              </Alert>

              {/* Action buttons */}
              <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleRetry}
                  color="primary"
                >
                  Try Again
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={() => window.location.reload()}
                >
                  Reload Page
                </Button>
              </Stack>

              {/* Developer details section */}
              {(this.props.showDetails || process.env.NODE_ENV === 'development') && (
                <Box sx={{ width: '100%', mt: 2 }}>
                  <Button
                    startIcon={<ExpandMoreIcon 
                      sx={{ 
                        transform: this.state.showErrorDetails ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.3s'
                      }} 
                    />}
                    onClick={this.toggleErrorDetails}
                    size="small"
                  >
                    {this.state.showErrorDetails ? 'Hide' : 'Show'} Technical Details
                  </Button>
                  
                  <Collapse in={this.state.showErrorDetails}>
                    <Box sx={{ mt: 2 }}>
                      <Alert severity="info" sx={{ textAlign: 'left' }}>
                        <AlertTitle>Technical Information</AlertTitle>
                        <Typography variant="body2" component="pre" sx={{ 
                          whiteSpace: 'pre-wrap', 
                          fontSize: '0.8rem',
                          fontFamily: 'monospace',
                          maxHeight: '200px',
                          overflow: 'auto'
                        }}>
                          {this.state.error?.stack || 'No stack trace available'}
                        </Typography>
                      </Alert>
                      
                      {this.state.errorInfo && (
                        <Alert severity="warning" sx={{ mt: 1, textAlign: 'left' }}>
                          <AlertTitle>Component Stack</AlertTitle>
                          <Typography variant="body2" component="pre" sx={{
                            whiteSpace: 'pre-wrap',
                            fontSize: '0.8rem',
                            fontFamily: 'monospace',
                            maxHeight: '200px',
                            overflow: 'auto'
                          }}>
                            {this.state.errorInfo.componentStack}
                          </Typography>
                        </Alert>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              )}

              {/* Help text */}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                If this problem persists, please contact support or try refreshing the page.
              </Typography>
            </Stack>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

// HOC for easier usage
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, 'children'>
) => {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
};

export default ErrorBoundary;
